import { ILatLng } from '..';
import {
	AssetDetailDto,
	AssetDto,
	LatLng,
	PaginatedListOfAssetDto
} from '../../models/models';
import { PagingActions, PagingOptions } from '../../models/types';
import {
	getAllAssets,
	getAllAssetsDetailed,
	getAsset,
	postAsset,
	putAsset,
	removeAllAssetsFromCache,
	removeAsset,
	removeAssetFromCache,
	searchAssetsByName
} from '../../services/AssetService';
import { getQueryString } from '../../utils';
import { AppState } from '../app.state';
import { AppThunkAction, AppThunkDispatch } from '../app.types';
import { history } from '../Store';
import {
	Asset,
	AssetAction,
	AssetActions,
	AssetListAction,
	LegendItem
} from './asset.types';

export const ASSET_CREATE: string = 'ASSET_CREATE';

export const ASSET_FETCH_LIST: string = 'ASSET_FETCH_LIST';
export const ASSET_FETCH: string = 'ASSET_FETCH';

export const ASSET_UPDATE: string = 'ASSET_UPDATE';

export const ASSET_DELETE: string = 'ASSET_DELETE';

export const ASSET_LOAD_DEFAULT: string = 'ASSET_LOAD_DEFAULT';
export const ASSET_LOAD_LIST: string = 'ASSET_LOAD_LIST';
export const ASSET_LOAD: string = 'ASSET_LOAD';

export const ASSET_LOAD_PAGING_OPTIONS: string = 'ASSET_LOAD_PAGING_OPTIONS';
export const ASSET_LIST_CHANGE_PAGE: string = 'ASSET_LIST_CHANGE_PAGE';

export const ASSET_REMOVE_FROM_CACHE: string = 'ASSET_REMOVE_FROM_CACHE';
export const ASSET_REMOVE_ALL_FROM_CACHE: string =
	'ASSET_REMOVE_ALL_FROM_CACHE';

export function createAsset(asset: Asset): AppThunkAction<void, AssetAction> {
	return async (dispatch: AppThunkDispatch<AssetAction>) => {
		dispatch({ type: ASSET_CREATE });

		const newAssetId: string = await postAsset(asset);
		const newAsset: AssetDetailDto = await getAsset(newAssetId);

		history.replace(`/manage/assets/${newAssetId}`);
		dispatch(loadAsset(newAsset));
	};
}

export function fetchDetailedAssetList(): AppThunkAction<void, AssetActions> {
	return async (dispatch: AppThunkDispatch<AssetActions | PagingActions>) => {
		dispatch({ type: ASSET_FETCH_LIST });

		const result: AssetDetailDto[] = await getAllAssetsDetailed();

		dispatch(loadDetailedAssetList(result));
	};
}

export function searchAssets(
	searchValue: string
): AppThunkAction<void, AssetListAction> {
	return async (dispatch: AppThunkDispatch<AssetListAction>) => {
		dispatch({ type: ASSET_FETCH_LIST });

		const assets: AssetDetailDto[] =
			searchValue === ''
				? await getAllAssetsDetailed()
				: await searchAssetsByName(searchValue);

		dispatch(loadDetailedAssetList(assets));
	};
}

export function fetchAssetList(
	pageIndex: number = 0,
	pageSize: number = 50
): AppThunkAction<void, AssetActions | PagingActions> {
	return async (
		dispatch: AppThunkDispatch<AssetActions | PagingActions>,
		getState: () => AppState
	) => {
		dispatch({ type: ASSET_FETCH_LIST });

		const state: AppState = getState();
		const queryString: string = getQueryString(
			pageIndex,
			pageSize,
			state.contentManager.sort,
			state.contentManager.filter
		);
		const result: PaginatedListOfAssetDto = await getAllAssets(queryString);

		const pagingOptions: PagingOptions = {
			currentPage: result.pageIndex,
			totalPages: result.totalPages,
			pageSize: pageSize
		};

		dispatch(loadAssetList(result.items as AssetDto[]));
		dispatch({ type: ASSET_LOAD_PAGING_OPTIONS, payload: pagingOptions });
	};
}

export function fetchAsset(id: string): AppThunkAction<void, AssetAction> {
	return async (dispatch: AppThunkDispatch<AssetAction>) => {
		dispatch({ type: ASSET_FETCH });

		const asset: AssetDetailDto = await getAsset(id);

		dispatch(loadAsset(asset));
	};
}

export function updateAsset(asset: Asset): AppThunkAction<void, AssetAction> {
	return async (dispatch: AppThunkDispatch<AssetAction>) => {
		dispatch({ type: ASSET_UPDATE });
		await putAsset(asset);
		const updatedAsset: AssetDetailDto = await getAsset(asset.id as string);
		dispatch(loadAsset(updatedAsset));
	};
}

export function deleteAsset(id: string): AppThunkAction<void, AssetAction> {
	return async (dispatch: AppThunkDispatch<AssetAction>) => {
		dispatch({ type: ASSET_DELETE });
		await removeAsset(id);
		dispatch(fetchAssetList());
	};
}

export function deleteAssetFromCache(
	casperId: string
): AppThunkAction<void, AssetAction> {
	return async (dispatch: AppThunkDispatch<AssetAction>) => {
		dispatch({ type: ASSET_REMOVE_FROM_CACHE });
		await removeAssetFromCache(casperId);
	};
}

export function deleteAllAssetsFromCache(): AppThunkAction<void, AssetAction> {
	return async (dispatch: AppThunkDispatch<AssetAction>) => {
		dispatch({ type: ASSET_REMOVE_ALL_FROM_CACHE });
		await removeAllAssetsFromCache();
	};
}

export function changeAssetPage(
	page: number
): AppThunkAction<void, PagingActions> {
	return (
		dispatch: AppThunkDispatch<PagingActions>,
		getState: () => AppState
	) => {
		const state = getState();
		dispatch({ type: ASSET_LIST_CHANGE_PAGE, payload: page });

		dispatch(fetchAssetList(page, state.assets.pagingOptions.pageSize));
	};
}

export function loadDefaultAsset(): AssetAction {
	return {
		type: ASSET_LOAD_DEFAULT
	};
}

export function loadDetailedAssetList(
	dtoList: AssetDetailDto[]
): AssetListAction {
	const assetList: Asset[] = dtoList.map(assetDetailDtoToAsset);
	return {
		type: ASSET_LOAD_LIST,
		payload: assetList
	};
}

export function loadAssetList(dtoList: AssetDto[]): AssetListAction {
	const assetList: Asset[] = dtoList.map(assetDtoToAsset);
	return {
		type: ASSET_LOAD_LIST,
		payload: assetList
	};
}

export function loadAsset(dto: AssetDetailDto): AssetAction {
	const asset: Asset = assetDetailDtoToAsset(dto);
	return {
		type: ASSET_LOAD,
		payload: asset
	};
}

function assetDetailDtoToAsset(dto: AssetDetailDto): Asset {
	return {
		...dto,
		id: dto.id,
		assetId: dto.assetId as string,
		assetName: dto.assetName as string,
		projection: dto.projection as string,
		corner: dto.corner
			? latLngToLatLngInterface(dto.corner as LatLng)
			: undefined,
		oppositeCorner: dto.oppositeCorner
			? latLngToLatLngInterface(dto.oppositeCorner as LatLng)
			: undefined,
		projectedCorner: dto.projectedCorner
			? latLngToLatLngInterface(dto.projectedCorner as LatLng)
			: undefined,
		projectedOppositeCorner: dto.projectedOppositeCorner
			? latLngToLatLngInterface(dto.projectedOppositeCorner as LatLng)
			: undefined,
		imageUrl: dto.imageUrl as string,
		legend: dto.legend,
		legendItems: dto.legend ? legendStringToLegendItems(dto.legend) : [],
		legendAssetId: dto.legendAssetId
	};
}

function assetDtoToAsset(dto: AssetDto): Asset {
	return {
		...dto,
		id: dto.id,
		assetName: dto.assetName as string,
		assetId: '',
		corner: { lat: 0, lng: 0 },
		oppositeCorner: { lat: 0, lng: 0 },
		projection: '',
		useProjection: false,
		licences: dto.licences
	};
}

function latLngToLatLngInterface(latLng: LatLng): ILatLng {
	return {
		lat: Number(latLng.lat as string),
		lng: Number(latLng.lng as string)
	};
}

export function legendStringToLegendItems(legend: string): LegendItem[] {
	const splitList: string[] = legend.split('|');
	return splitList.map(getLegendItemFromString);
}

function getLegendItemFromString(layer: string): LegendItem {
	const split: string[] = layer.split(';');
	return { key: split[0], value: split[1] };
}
